import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from 'react';
import User from '../models/user';
import { useQueryCurrentUser } from '@authentication/queries/userQueries';

import { LocalStorageKeys } from '@authentication/constants';
import { getItem } from '@/lib/storageUtil';
import Candidate from '@models/Candidate';

interface UserContextData {
  user: User | null;
  candidates: Candidate[] | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  setCandidates: Dispatch<SetStateAction<Candidate[] | null>>;
  pageTitle: string;
  setPageTitle: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const UserContext = createContext<UserContextData | undefined>(
  undefined,
);

const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [pageTitle, setPageTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const storedUserId = getItem(LocalStorageKeys.USER_ID);
  const { data, isLoading: queryIsLoading } = useQueryCurrentUser(true);

  useEffect(() => {
    if (user ?? queryIsLoading) return;
    if (data?.data) {
      setUser(data?.data);
    }
    setIsLoading(false); // Set isLoading to false once data is fetched or queryIsLoading becomes false
  }, [queryIsLoading]);

  useEffect(() => {
    if (!storedUserId) setIsLoading(false);
  }, [storedUserId]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isLoading,
        setIsLoading,
        pageTitle,
        setPageTitle,
        candidates,
        setCandidates,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
